import { BLUEPRINTS, RED_FLAGS, type BlueprintToken, type RedFlagToken } from './catalog.js';

// ── Blueprint thesis engine (thin, pure) ─────────────────────────────────────
// Turns a Prescription pick + the buyer context into a buyer-relative THESIS:
// which named blueprints fire and which red-flags — the "why THIS target, for
// YOU". It only READS evidence already computed (canonical scores, role, region)
// and the buyer's mandate/portfolio — it invents no new score (invariant 1) and
// is computed in-build, never precomputed (invariant 6). Isomorphic so the web,
// the API, and the memo (dossier) all call the same logic.
//
// v1 fires the patterns derivable from the fields a build pick already carries
// (role, quality tier, seller-readiness, origination signal, region) + the
// portfolio footprint. Specialism-fit / value-trap / saturated-roll-up need
// fields not yet on the pick (specialisms, charges, cell CR5) and land as those
// are threaded through — see BUILD_PLAN.md.

const BP_LABEL = Object.fromEntries(BLUEPRINTS.map((b) => [b.token, b.label])) as Record<
  BlueprintToken,
  string
>;
const RF_LABEL = Object.fromEntries(RED_FLAGS.map((r) => [r.token, r.label])) as Record<
  RedFlagToken,
  string
>;

/** The subset of a build pick the thesis reads. */
export interface ThesisPick {
  role: 'platform' | 'bolton';
  turnoverEur: number | null;
  qualityTier: string | null; // 'Strong' | 'Good' | 'Weak' | 'Not rated' | null
  sellerReadiness: number | null; // 0..100
  originationSignal: number | null; // 0..100 (within-market percentile)
  region: string | null;
}

/** Buyer context — the mandate (what they're building) + portfolio footprint
 *  (what they already operate). Both optional: with neither, only the intrinsic
 *  patterns fire. */
export interface ThesisContext {
  /** Regions the buyer already operates in (from their portfolio). */
  ownedRegions: ReadonlySet<string>;
  /** Whether a mandate is attached (enables mandate-fit framing). */
  hasMandate: boolean;
}

export interface FiredPattern {
  token: string;
  label: string;
  /** Contextual, evidence-bound one-liner — safe to show verbatim. */
  note: string;
}

export interface PickThesis {
  blueprints: FiredPattern[];
  redFlags: FiredPattern[];
}

const bp = (token: BlueprintToken, note: string): FiredPattern => ({
  token,
  label: BP_LABEL[token],
  note,
});
const rf = (token: RedFlagToken, note: string): FiredPattern => ({
  token,
  label: RF_LABEL[token],
  note,
});

// Thresholds — deliberately coarse/ordinal (structural, not a fourth score).
// Exported so the conditions layer reads the SAME lines (one vocabulary, two
// layers — a condition and its source blueprint must never disagree on when
// they fire).
export const SELLER_HIGH = 65; // top-ish of the 0-100 seller-readiness scale
export const SIGNAL_MODEST = 50; // below the within-market median

/** Compute the fired blueprints + red-flags for one pick, given the buyer
 *  context. Deterministic; returns empty arrays when nothing fires. */
export function computePickThesis(pick: ThesisPick, ctx: ThesisContext): PickThesis {
  const blueprints: FiredPattern[] = [];
  const redFlags: FiredPattern[] = [];

  // Structural role.
  if (pick.role === 'platform') {
    blueprints.push(bp('platform_anchor', 'Scaled base to build the platform around'));
  } else {
    blueprints.push(bp('bolt_on', 'Adds to the platform'));
  }

  // Intrinsic (from canonical scores).
  if (pick.sellerReadiness != null && pick.sellerReadiness >= SELLER_HIGH) {
    blueprints.push(
      bp('succession_sale', `Owner likely gettable (readiness ${pick.sellerReadiness})`),
    );
  }
  if (
    pick.qualityTier === 'Strong' &&
    pick.originationSignal != null &&
    pick.originationSignal < SIGNAL_MODEST
  ) {
    blueprints.push(bp('quality_arbitrage', 'Strong operator that looks under-recognised'));
  }
  if (pick.qualityTier === 'Weak') {
    blueprints.push(bp('distress_turnaround', 'Weak quality — a turnaround / improvement play'));
  }

  // Buyer-relative (from the portfolio footprint).
  if (pick.region && ctx.ownedRegions.size > 0) {
    if (ctx.ownedRegions.has(pick.region)) {
      redFlags.push(rf('cannibalisation', `Overlaps your existing ${pick.region} footprint`));
    } else {
      blueprints.push(bp('footprint_gap_filler', `Extends you into ${pick.region} (a gap)`));
    }
  }

  return { blueprints, redFlags };
}

/** Distinct blueprint/red-flag tokens fired across a whole build — used for the
 *  build-level rationale summary. */
export function summariseThesis(theses: PickThesis[]): {
  blueprints: Set<string>;
  redFlags: Set<string>;
  gapFillers: number;
  cannibalisations: number;
} {
  const blueprints = new Set<string>();
  const redFlags = new Set<string>();
  let gapFillers = 0;
  let cannibalisations = 0;
  for (const t of theses) {
    for (const b of t.blueprints) {
      blueprints.add(b.token);
      if (b.token === 'footprint_gap_filler') gapFillers += 1;
    }
    for (const r of t.redFlags) {
      redFlags.add(r.token);
      if (r.token === 'cannibalisation') cannibalisations += 1;
    }
  }
  return { blueprints, redFlags, gapFillers, cannibalisations };
}
